import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PACKS } from '@/data/templates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'checking', plan: '' });
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error' });
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/billing/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          await refresh();
          const pid = res.data.plan_id;
          const isPack = PACKS.some(p => p.slug === pid);
          setState({ status: 'paid', plan: pid, isPack });
          setTimeout(() => navigate(isPack ? '/' : '/account'), 2400);
          return;
        }
        if (res.data.status === 'expired') {
          setState({ status: 'expired' });
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 1800);
        } else {
          setState({ status: 'timeout' });
        }
      } catch (e) {
        setState({ status: 'error' });
      }
    };
    poll();
  }, [sessionId, refresh, navigate]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {state.status === 'checking' && (
        <>
          <Loader2 size={36} className="animate-spin mx-auto text-orange-600 mb-5" />
          <h1 className="font-display font-bold text-3xl tracking-tight">Confirming your payment…</h1>
          <p className="text-stone-500 mt-3">This usually takes a second.</p>
        </>
      )}
      {state.status === 'paid' && (
        <div data-testid="billing-paid">
          <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-5" />
          <h1 className="font-display font-bold text-4xl tracking-tight">
            {state.isPack ? 'Pack unlocked 🎉' : "You're Pro now 🎉"}
          </h1>
          <p className="text-stone-500 mt-3">
            {state.isPack
              ? 'Every document in your pack now downloads clean — no watermark. Taking you back to your packs…'
              : 'Welcome to unlimited templates, no watermarks, and AI drafts. Taking you to your account…'}
          </p>
        </div>
      )}
      {(state.status === 'expired' || state.status === 'timeout' || state.status === 'error') && (
        <div data-testid="billing-error">
          <XCircle size={48} className="mx-auto text-red-500 mb-5" />
          <h1 className="font-display font-bold text-3xl tracking-tight">Something went sideways</h1>
          <p className="text-stone-500 mt-3">We couldn't confirm the payment. If you were charged, refresh in a minute.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Link to="/pricing" className="px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm font-medium btn-clay">Back to pricing</Link>
            <Link to="/account" className="px-5 py-2.5 rounded-full border border-stone-300 text-sm font-medium hover:bg-stone-100">Open account</Link>
          </div>
        </div>
      )}
    </div>
  );
}
